import React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import DebouncedInput from './DebounceInput';
import Filter, { dateBetweenFilterFn } from './FilterComponent';
import fuzzyFilter from './Filters';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import './Table.css';

/**
 * ServerTable
 *
 * Props:
 *  - columns: tanstack columns
 *  - fetchData: async ({ pageIndex, pageSize, sorting, columnFilters, globalFilter, signal }) => { items, totalItems }
 *  - tableClass, containerClass, headerClass, filtersHeaderClass: styling hooks (same as your Table)
 *  - enableGlobalFilter: boolean
 *  - initialPageSize?: number (default 20)
 *  - fillAvailableHeight?: boolean (keeps pagination outside a two-axis table viewport)
 *  - initialSorting, enableSortingRemoval, enableMultiSort, sortDescFirst: server sorting configuration
 *  - queryDebounceMs?: number (default 0); showTotalItems?: boolean (default false)
 */
export default function ServerTable({
  columns,
  fetchData,
  tableClass = '',
  containerClass = 'table-responsive',
  headerClass = 'table-light',
  filtersHeaderClass = '',
  enableGlobalFilter = false,
  initialPageSize = 10,
  initialSorting = [],
  enableSortingRemoval = true,
  enableMultiSort = true,
  sortDescFirst,
  queryDebounceMs = 0,
  showTotalItems = false,
  onRowDoubleClick,
  getRowClassName,
  onQueryStateChange,
  onBeforeQueryChange,
  fillAvailableHeight = false,
  globalFilterPlaceholder = 'Search all columns...',
  pageSizeOptions = [10, 20, 30, 40, 50, 100],
  refreshKey,
}) {
  const [data, setData] = React.useState([]);
  const [totalItems, setTotalItems] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const [globalFilter, setGlobalFilter] = React.useState('');
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [sorting, setSorting] = React.useState(initialSorting);
  const [pagination, setPagination] = React.useState({ pageIndex: 0, pageSize: initialPageSize });
  const [filterResetKey, setFilterResetKey] = React.useState(0);
  const queryChangePendingRef = React.useRef(false);

  const requestQueryChange = React.useCallback(async (type, current, updater, apply, resetPage = false) => {
    const next = typeof updater === 'function' ? updater(current) : updater;
    if (JSON.stringify(next) === JSON.stringify(current) || queryChangePendingRef.current) return;

    // Without a confirmation hook, apply synchronously so simultaneous filters
    // compose against the latest state instead of being dropped by the guard.
    if (!onBeforeQueryChange) {
      apply(updater);
      if (resetPage) setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      return;
    }

    queryChangePendingRef.current = true;
    let allowed = true;
    try {
      allowed = (await onBeforeQueryChange?.({ type, current, next })) !== false;
    } finally {
      queryChangePendingRef.current = false;
    }

    if (!allowed) {
      if (type === 'columnFilters' || type === 'globalFilter') setFilterResetKey((key) => key + 1);
      return;
    }

    apply(next);
    if (resetPage) setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [onBeforeQueryChange]);

  const pageCount = React.useMemo(
    () => Math.max(1, Math.ceil(totalItems / pagination.pageSize)),
    [totalItems, pagination.pageSize]
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      globalFilter,
      columnFilters,
      sorting,
      pagination,
    },
    manualPagination: true,
    pageCount,
    manualSorting: true,
    manualFiltering: true,
    enableSortingRemoval,
    enableMultiSort,
    ...(sortDescFirst === undefined ? {} : { sortDescFirst }),
    // We still expose the same filterFns so your FilterComponent works,
    filterFns: {
      fuzzy: fuzzyFilter,
      dateBetweenFilterFn,
    },
    onGlobalFilterChange: (updater) => requestQueryChange('globalFilter', globalFilter, updater, setGlobalFilter, true),
    onColumnFiltersChange: (updater) => requestQueryChange('columnFilters', columnFilters, updater, setColumnFilters, true),
    onSortingChange: (updater) => requestQueryChange('sorting', sorting, updater, setSorting, true),
    onPaginationChange: (updater) => requestQueryChange('pagination', pagination, updater, setPagination),
    getCoreRowModel: getCoreRowModel(),
    debugTable: false,
  });

  // Fetch server data when table state changes
  React.useEffect(() => {
    let ignore = false;
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    const fetchPage = async () => {
      const queryState = {
        pageIndex: table.getState().pagination.pageIndex,
        pageSize: table.getState().pagination.pageSize,
        sorting: table.getState().sorting,
        columnFilters: table.getState().columnFilters,
        globalFilter: table.getState().globalFilter,
      };
      onQueryStateChange?.(queryState);
      setIsLoading(true);
      setError(null);
      try {
        const res = await fetchData({ ...queryState, signal: controller.signal });
        if (!ignore) {
          setData(res.items ?? []);
          setTotalItems(res.totalItems ?? 0);
          const lastPage = Math.max(0, Math.ceil((res.totalItems ?? 0) / pagination.pageSize) - 1);
          if (pagination.pageIndex > lastPage) setPagination(prev => ({ ...prev, pageIndex: lastPage }));
        }
      } catch (err) {
        if (!ignore) {
          setError(err);
          setData([]);
          setTotalItems(0);
          console.error('ServerTable fetch error:', err);
        }
      } finally {
        if (!ignore) setIsLoading(false);
      }
    };
    const timeout = queryDebounceMs ? setTimeout(fetchPage, queryDebounceMs) : null;
    if (!queryDebounceMs) fetchPage();
    return () => { ignore = true; clearTimeout(timeout); controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilter, columnFilters, sorting, pagination.pageIndex, pagination.pageSize, fetchData, refreshKey, queryDebounceMs]);

  const renderColumnHeaderRows = (rowClassName = '') => table.getHeaderGroups().map(headerGroup => (
    <tr key={`columns-${headerGroup.id}`} className={rowClassName}>
      {headerGroup.headers.map(header => (
        <th
          key={header.id}
          aria-sort={header.column.getCanSort() ? (header.column.getIsSorted() === 'asc' ? 'ascending' : header.column.getIsSorted() === 'desc' ? 'descending' : 'none') : undefined}
          colSpan={header.colSpan}
          className="border-bottom-0 text-nowrap"
          style={header.column.columnDef?.meta?.headerStyle}
        >
          {header.isPlaceholder ? null : (
            <div
              role={header.column.getCanSort() ? 'button' : undefined}
              tabIndex={header.column.getCanSort() ? 0 : undefined}
              onKeyDown={event => {
                if (header.column.getCanSort() && (event.key === 'Enter' || event.key === ' ')) {
                  event.preventDefault();
                  header.column.getToggleSortingHandler()?.(event);
                }
              }}
              className={
                header.column.getCanSort()
                  ? 'cursor-pointer select-none py-2 px-1'
                  : 'py-2 px-1'
              }
              onClick={header.column.getToggleSortingHandler()}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
              {{
                asc: <FontAwesomeIcon className="ms-2 text-accent2" icon={faSortUp} />,
                desc: <FontAwesomeIcon className="ms-2 text-accent2" icon={faSortDown} />,
              }[header.column.getIsSorted()] ??
                (header.column.getCanSort() ? (
                  <FontAwesomeIcon className="ms-2 text-accent2" icon={faSort} />
                ) : null)}
            </div>
          )}
        </th>
      ))}
    </tr>
  ));

  const renderFilterRows = (rowClassName = '') => table.getHeaderGroups().map(headerGroup => (
    <tr key={`filters-${headerGroup.id}`} className={rowClassName}>
      {headerGroup.headers.map(header => (
        <th
          key={header.id}
          colSpan={header.colSpan}
          style={header.column.columnDef?.meta?.filterStyle}
        >
          {header.isPlaceholder ? null : (
            header.column.getCanFilter() ? (
              <div>
                <Filter
                  key={`${header.column.id}-${filterResetKey}`}
                  column={header.column}
                  table={table}
                  onFilterApplied={() => table.setPageIndex(0)}
                />
              </div>
            ) : null
          )}
        </th>
      ))}
    </tr>
  ));

  const tableElement = (
    <table className={`table ${tableClass}`} aria-busy={isLoading}>
      {fillAvailableHeight ? (
        <thead className="server-table-sticky-header">
          {renderColumnHeaderRows(headerClass)}
          {renderFilterRows(filtersHeaderClass)}
        </thead>
      ) : (
        <>
          <thead className={`${headerClass}`}>
            {renderColumnHeaderRows()}
          </thead>
          <thead className={`${filtersHeaderClass}`}>
            {renderFilterRows()}
          </thead>
        </>
      )}

      <tbody className="table-group-divider">
        {isLoading && (
          <tr>
            <td colSpan={columns.length}>
              <div className="py-4 text-center text-muted">Loading…</div>
            </td>
          </tr>
        )}
        {!isLoading && !error && data.length === 0 && (
          <tr>
            <td colSpan={columns.length}>
              <div className="py-4 text-center text-muted">No records found</div>
            </td>
          </tr>
        )}
        {!isLoading && !error &&
          table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={getRowClassName ? getRowClassName(row.original, row) : ''}
              onDoubleClick={onRowDoubleClick ? () => onRowDoubleClick(row.original, row) : undefined}
              style={onRowDoubleClick ? { cursor: 'pointer' } : undefined}
            >
              {row.getVisibleCells().map(cell => (
                <td key={cell.id} style={cell.column.columnDef?.meta?.cellStyle}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
      </tbody>
    </table>
  );

  const globalFilterControl = enableGlobalFilter && (
    <div>
      <DebouncedInput
        value={globalFilter ?? ''}
        onChange={value => {
          requestQueryChange('globalFilter', globalFilter, value, setGlobalFilter, true);
        }}
        className="p-2 mb-2 form-control form-control-sm"
        placeholder={globalFilterPlaceholder}
        aria-label={globalFilterPlaceholder}
      />
    </div>
  );

  const paginationElement = (
    <div
      className={`d-flex align-items-center gx-2 ${fillAvailableHeight ? 'server-table-pagination' : 'mt-3'}`}
    >
      <nav aria-label="Table pagination">
        <ul className={`pagination m-0 ${tableClass.includes('table-sm') ? 'pagination-sm' : ''}`}>
          <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
            <button className="page-link" disabled={isLoading || pagination.pageIndex === 0} onClick={() => table.setPageIndex(0)} aria-label="First">
              «
            </button>
          </li>
          <li className={`page-item ${pagination.pageIndex === 0 ? 'disabled' : ''}`}>
            <button className="page-link" disabled={isLoading || pagination.pageIndex === 0} onClick={() => table.previousPage()} aria-label="Previous">
              &lt;
            </button>
          </li>
          <li className={`page-item ${pagination.pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
            <button className="page-link" disabled={isLoading || pagination.pageIndex >= pageCount - 1} onClick={() => table.nextPage()} aria-label="Next">
              &gt;
            </button>
          </li>
          <li className={`page-item ${pagination.pageIndex >= pageCount - 1 ? 'disabled' : ''}`}>
            <button className="page-link" disabled={isLoading || pagination.pageIndex >= pageCount - 1} onClick={() => table.setPageIndex(pageCount - 1)} aria-label="Last">
              »
            </button>
          </li>
        </ul>
      </nav>

      <span className="d-flex align-items-center gx-1 ms-2">
        <div>Page</div>
        <strong className="ms-1">
          {pagination.pageIndex + 1} of {pageCount}
        </strong>
      </span>

      {showTotalItems && <span className="ms-2">{totalItems} records</span>}

      <span className="d-flex align-items-center gx-1 ms-2">
        | Go to page:
        <input
          type="number"
          min={1}
          max={pageCount}
          aria-label="Go to page"
          value={pagination.pageIndex + 1}
          onChange={e => {
            const p = e.target.value ? Number(e.target.value) - 1 : 0;
            table.setPageIndex(Number.isFinite(p) ? Math.max(0, Math.min(pageCount - 1, p)) : 0);
          }}
          className={`form-control w-auto ms-1 ${tableClass.includes('table-sm') ? 'form-control-sm' : ''}`}
        />
      </span>

      <select
        aria-label="Page size"
        value={pagination.pageSize}
        onChange={e => requestQueryChange('pagination', pagination, { pageSize: Number(e.target.value), pageIndex: 0 }, setPagination)}
        className={`form-select w-auto ms-2 ${tableClass.includes('table-sm') ? 'form-select-sm' : ''}`}
      >
        {pageSizeOptions.map(ps => (
          <option key={ps} value={ps}>
            Show {ps}
          </option>
        ))}
      </select>

      {error && <span role="alert" className="ms-3 text-danger small">Failed to load data.</span>}
    </div>
  );

  if (fillAvailableHeight) {
    return (
      <div className="server-table server-table-fill">
        {globalFilterControl && <div className="server-table-global-filter">{globalFilterControl}</div>}
        <div
          className={`w-100 ${containerClass} server-table-viewport`}
          role="region"
          aria-label="Scrollable table"
          tabIndex={0}
        >
          {tableElement}
        </div>
        {paginationElement}
      </div>
    );
  }

  return (
    <>
      <div className={`w-100 ${containerClass}`}>
        {globalFilterControl}
        <div className="h-2" />
        {tableElement}
      </div>
      {paginationElement}
    </>
  );
}
